/**
 * One side of a spread drawn as a picture, by a ThumbGenerator of its own — the service one is
 * shared with the save, which sends what it draws as the creation's cover thumbnail. One drawing at
 * a time : the generator has a single canvas.
 *
 *   new desktop.common.preview.PageArtwork(owner, model, ratio)
 *   draw(element, side, options) → Promise<object URL | null>   complete : false when a picture of the
 *     page did not load in time (it was left out), or the page itself took too long — worth drawing again.
 *     The photos missing are then cut out of the picture, transparent : the face shows through, blinking
 *   blank(width, height, color, options) → Promise<object URL | null>   a page with nothing printed
 *   missing   after an incomplete draw(), the zones of the photos missing (see options.missing)
 *   upscaled  after draw(), whether a photo was drawn larger than the picture loaded for it
 *   waiting   after an incomplete draw(), the photos left out because the browser has no picture of
 *             them yet (still being read, or a HEIC still being sent) : [models.Photo]
 *   abort()   stops the drawing running, its photos with it : draw() answers null, aborted true
 *
 * side : "left" | "right" (the cover : back | front). options :
 *   - fold : the edge of the picture at the binding, "left" | "right", shaded in the picture
 *     itself — a layer over the picture (a ::after, a div, even a mask) makes Chrome paint the
 *     preview's leaves in the order of the page rather than their depth, the last leaf over the cover
 *   - holes : the spiral's punched holes, cut out of the picture (BookBinding.holes()), which is
 *     then a WebP with transparency rather than a JPEG
 *   - full : the photos at full resolution rather than the medium version (draw() only)
 *   - progress(done, total) : draw() calls it as the photos of the page come, done of total
 *   - missing : zones cut out the same way (ThumbGenerator's thumb-generated missing, shares of the
 *     page, rotated around their top left corner), the photos that were still loading
 *
 * owner : the component listening (fx events go through a listener, and stop with it) ; ratio :
 * pixels per millimetre of the page. The pictures are kept as JPEG blob URLs rather than data URLs
 * (a hundred pages of base64 PNGs weigh on the memory), revoked by destroy().
 */
fx.ns("desktop.common.preview", class PageArtwork {

    // the binding's shadow : the share of the page's width it covers, its darkness at the fold
    get FOLD_WIDTH() { return 0.09; }
    get FOLD_SHADE() { return 0.16; }

    // how long a photo of the page may take to load before the page is drawn without it
    get IMAGE_TIMEOUT() { return 45000; }

    constructor(owner, model, ratio)
    {
        this.owner = owner;
        this.model = model;
        this.ratio = ratio || 3;
        this.urls = [];

        let Generator = fx._services && fx._services.thumbgenerator;
        if (Generator && typeof Generator != "function")
            Generator = Generator.constructor;

        if (Generator)
        {
            this.generator = new Generator(model);
            this.generator.ratio = this.ratio;

            // a page on the table waits for its photos, however slow the connection : turning the
            // page stops it (abort()). A full page photo of several MB takes well over the 5 s a
            // cover thumbnail gives it, and would leave the page blank until drawn again
            this.generator.imageTimeOut = this.IMAGE_TIMEOUT;
            this.generator.stallTimeOut = this.IMAGE_TIMEOUT + 5000;
        }
    }

    draw(element, side, options)
    {
        this.complete = false;
        this.aborted = false;
        this.missing = [];
        this.upscaled = false;
        this.waiting = [];

        let progress = (e) => {
            if (options && options.progress && e.detail)
                options.progress(e.detail.done, e.detail.total);
        };

        return new Promise((resolve) => {
            if (!this.generator || this.destroyed)
                return resolve(null);

            let finished = false;
            let done = (e) => {
                if (finished)
                    return;

                finished = true;
                this._abort = null;
                this.owner.off("thumb-generated", done, this.generator);
                this.owner.off("thumb-progress", progress, this.generator);
                this.complete = !!(e.detail && e.detail.thumb && e.detail.complete);
                this.upscaled = !!(e.detail && e.detail.upscaled);
                this.waiting = (!this.complete && e.detail && e.detail.waiting) || [];

                let missing = (!this.complete && e.detail && e.detail.missing) || [];
                this.missing = missing;
                if (missing.length)
                    options = Object.assign({}, options, { missing : missing });

                resolve(this.destroyed ? null : this._picture(e.detail && e.detail.thumb, options));
            };
            this.owner.on("thumb-generated", done, this.generator);
            this.owner.on("thumb-progress", progress, this.generator);
            this._abort = () => {
                this.aborted = true;
                this.generator.abort();
                done({});
            };
            this.generator.fullQuality = !!(options && options.full);
            this.generator.generate(this.model.creation, element, side, false);

            // generate() refused (already drawing) or failed : it returned without a word
            if (!finished && !this.generator.generating)
                done({});
        });
    }

    abort()
    {
        if (this._abort)
            this._abort();
    }

    blank(width, height, color, options)
    {
        let canvas = document.createElement("canvas"),
            context = canvas.getContext("2d");

        canvas.width = Math.round(width);
        canvas.height = Math.round(height);
        context.fillStyle = color;
        context.fillRect(0, 0, canvas.width, canvas.height);

        return this._finish(canvas, options, null);
    }

    // the drawing with its binding's shadow and holes, as a blob URL ; the drawing as it is if that fails
    _picture(dataURL, options)
    {
        if (!dataURL)
            return Promise.resolve(null);

        return new Promise((resolve) => {
            let image = new Image();

            image.onerror = () => resolve(dataURL);
            image.onload = () => {
                try {
                    let canvas = document.createElement("canvas");
                    canvas.width = image.naturalWidth;
                    canvas.height = image.naturalHeight;
                    canvas.getContext("2d").drawImage(image, 0, 0);

                    this._finish(canvas, options, dataURL).then(resolve);
                }
                catch (e) {
                    resolve(dataURL);
                }
            };
            image.src = dataURL;
        });
    }

    _finish(canvas, options, fallback)
    {
        options = options || {};

        return new Promise((resolve) => {
            try {
                let context = canvas.getContext("2d"),
                    width = canvas.width,
                    height = canvas.height,
                    fold = options.fold,
                    holes = options.holes,
                    missing = options.missing || [];

                if (fold)
                {
                    let shade = width * this.FOLD_WIDTH,
                        x = (fold == "left") ? 0 : width - shade,
                        gradient = context.createLinearGradient(fold == "left" ? 0 : width, 0, fold == "left" ? shade : width - shade, 0);

                    gradient.addColorStop(0, "rgba(0, 0, 0, " + this.FOLD_SHADE + ")");
                    gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
                    context.fillStyle = gradient;
                    context.fillRect(x, 0, shade, height);
                }

                if (holes)
                {
                    let x = (holes.edge == "left") ? holes.x * width : width - holes.x * width,
                        r = holes.r * width;

                    // a faint rim, then the hole itself
                    context.fillStyle = "rgba(0, 0, 0, .3)";
                    holes.ys.forEach((y) => { context.beginPath(); context.arc(x, y * height, r * 1.25, 0, Math.PI * 2); context.fill(); });

                    context.globalCompositeOperation = "destination-out";
                    holes.ys.forEach((y) => { context.beginPath(); context.arc(x, y * height, r, 0, Math.PI * 2); context.fill(); });
                    context.globalCompositeOperation = "source-over";
                }

                if (missing.length)
                {
                    context.globalCompositeOperation = "destination-out";
                    context.fillStyle = "#000";
                    missing.forEach((zone) => {
                        context.save();
                        context.translate(zone.x * width, zone.y * height);
                        context.rotate((zone.rotation || 0) * Math.PI / 180);
                        context.fillRect(0, 0, zone.width * width, zone.height * height);
                        context.restore();
                    });
                    context.globalCompositeOperation = "source-over";
                }

                canvas.toBlob((blob) => {
                    if (!blob || this.destroyed)
                        return resolve(this.destroyed ? null : fallback);

                    let url = URL.createObjectURL(blob);
                    this.urls.push(url);
                    resolve(url);
                }, (holes || missing.length) ? "image/webp" : "image/jpeg", 0.92);
            }
            catch (e) {
                resolve(fallback);
            }
        });
    }

    destroy()
    {
        this.destroyed = true;
        this.urls.forEach((url) => URL.revokeObjectURL(url));
        this.urls = [];
    }
});
